import React, { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Dimensions, Easing, StyleSheet, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Svg, { Circle, Defs, Line, LinearGradient as SvgLinearGradient, Pattern, RadialGradient, Rect, Stop } from 'react-native-svg';
import { useTheme } from '../../theme/useTheme';

const { width, height } = Dimensions.get('window');

const AMBIENT_W = width + 180;
const AMBIENT_H = height + 180;

const GRAIN_DOTS = Array.from({ length: 90 }, (_, index) => {
  const x = (index * 47 + 13) % 1000;
  const y = (index * 83 + 29) % 1000;
  const opacity = 0.16 + ((index * 19) % 28) / 100;
  return { x: `${x / 10}%`, y: `${y / 10}%`, opacity };
});

/**
 * Premium metallic-glass chat backdrop: a diagonal charcoal/midnight-blue/
 * graphite base "brushed titanium" gradient, a barely-visible directional
 * grain texture, three low-opacity ambient light pools (Tonyte orange
 * bottom-right, cool silver top-left, deep indigo center), and one diagonal
 * "polished metal" highlight band that all drift together on a single slow
 * (52s) transform-only Animated loop — cheap (opacity/translate/scale on the
 * GPU compositor, nothing repaints), static SVG defs are built once.
 */
const ChatAtmosphere: React.FC = React.memo(() => {
  const { theme } = useTheme();
  const drift = useRef(new Animated.Value(0)).current;
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled()
      .then(enabled => {
        if (mounted) setReduceMotion(enabled);
      })
      .catch(() => undefined);

    const subscription = AccessibilityInfo.addEventListener?.('reduceMotionChanged', setReduceMotion);
    return () => {
      mounted = false;
      subscription?.remove?.();
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;

    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(drift, {
          toValue: 1,
          duration: 52000,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(drift, {
          toValue: 0,
          duration: 52000,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );

    animation.start();
    return () => animation.stop();
  }, [drift, reduceMotion]);

  const ambientTransform = {
    transform: [
      { translateX: drift.interpolate({ inputRange: [0, 1], outputRange: [-5, 6] }) },
      { translateY: drift.interpolate({ inputRange: [0, 1], outputRange: [4, -6] }) },
      { scale: 1.015 },
    ],
  };

  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      {/* Metallic base — brushed titanium beneath dark glass */}
      <LinearGradient
        colors={theme.isDark
          ? ['#1B2028', '#12161F', '#0B1220', '#0A0D14', '#08090C']
          : ['#EEF5F1', '#F7F1E8', '#F5EEE4', '#EDE3D7', '#E8DED2']}
        locations={[0, 0.22, 0.48, 0.74, 1]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />

      {/* Brushed-metal grain — near-invisible directional texture, static (no per-frame cost) */}
      <Svg width="100%" height="100%" style={StyleSheet.absoluteFill}>
        <Defs>
          <Pattern id="brushedGrain" width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(18)">
            <Line x1={0} y1={0} x2={0} y2={6} stroke={theme.isDark ? '#FFFFFF' : '#715D49'} strokeOpacity={theme.isDark ? 0.03 : 0.022} strokeWidth={0.6} />
          </Pattern>
        </Defs>
        <Rect width="100%" height="100%" fill="url(#brushedGrain)" />
      </Svg>

      {/* Ambient light pools + diagonal metallic shine, all sharing one slow drift transform */}
      <Animated.View style={[styles.ambientLayer, ambientTransform]}>
        <Svg width="100%" height="100%" viewBox={`0 0 ${AMBIENT_W} ${AMBIENT_H}`} preserveAspectRatio="none">
          <Defs>
            <RadialGradient id="tonyteOrange" cx="86%" cy="90%" rx="60%" ry="46%">
              <Stop offset="0%" stopColor="#F27D26" stopOpacity={theme.isDark ? 0.16 : 0.10} />
              <Stop offset="34%" stopColor="#F27D26" stopOpacity="0.06" />
              <Stop offset="72%" stopColor="#F27D26" stopOpacity="0.014" />
              <Stop offset="100%" stopColor="#F27D26" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient id="coolSilver" cx="10%" cy="8%" rx="60%" ry="46%">
              <Stop offset="0%" stopColor={theme.isDark ? '#C3D0E4' : '#FFFFFF'} stopOpacity={theme.isDark ? 0.12 : 0.32} />
              <Stop offset="40%" stopColor="#B9C6DA" stopOpacity="0.045" />
              <Stop offset="78%" stopColor="#B9C6DA" stopOpacity="0.01" />
              <Stop offset="100%" stopColor="#B9C6DA" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient id="deepIndigo" cx="50%" cy="48%" rx="60%" ry="62%">
              <Stop offset="0%" stopColor={theme.isDark ? '#4B4FA0' : '#159B9A'} stopOpacity={theme.isDark ? 0.07 : 0.045} />
              <Stop offset="55%" stopColor="#4B4FA0" stopOpacity="0.025" />
              <Stop offset="100%" stopColor="#4B4FA0" stopOpacity="0" />
            </RadialGradient>
            <SvgLinearGradient id="metallicShine" x1="0%" y1="100%" x2="100%" y2="0%">
              <Stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
              <Stop offset="44%" stopColor="#FFFFFF" stopOpacity="0" />
              <Stop offset="50%" stopColor={theme.isDark ? '#DCE6F5' : '#FFFFFF'} stopOpacity={theme.isDark ? 0.05 : 0.14} />
              <Stop offset="56%" stopColor="#FFFFFF" stopOpacity="0" />
              <Stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
            </SvgLinearGradient>
          </Defs>
          <Rect width={AMBIENT_W} height={AMBIENT_H} fill="url(#tonyteOrange)" />
          <Rect width={AMBIENT_W} height={AMBIENT_H} fill="url(#coolSilver)" />
          <Rect width={AMBIENT_W} height={AMBIENT_H} fill="url(#deepIndigo)" />
          <Rect width={AMBIENT_W} height={AMBIENT_H} fill="url(#metallicShine)" />
        </Svg>
      </Animated.View>

      {/* Edge vignette — quietly guides attention back to the conversation */}
      <Svg width="100%" height="100%" style={StyleSheet.absoluteFill} preserveAspectRatio="none">
        <Defs>
          <RadialGradient id="chatVignette" cx="50%" cy="46%" rx="72%" ry="74%">
            <Stop offset="0%" stopColor="#000000" stopOpacity="0" />
            <Stop offset="64%" stopColor={theme.isDark ? '#000000' : '#7E684F'} stopOpacity={theme.isDark ? 0.08 : 0.025} />
            <Stop offset="100%" stopColor={theme.isDark ? '#000000' : '#7E684F'} stopOpacity={theme.isDark ? 0.4 : 0.12} />
          </RadialGradient>
        </Defs>
        <Rect width="100%" height="100%" fill="url(#chatVignette)" />
      </Svg>

      <View style={styles.edgeShadeTop} />
      <View style={[styles.edgeShadeBottom, !theme.isDark && styles.edgeShadeBottomDaylight]} />

      {/* Fine cinematic grain — kills flat-color banding on OLED */}
      <Svg width="100%" height="100%" style={styles.grain} preserveAspectRatio="none">
        {GRAIN_DOTS.map((dot, index) => (
          <Circle key={index} cx={dot.x} cy={dot.y} r={index % 3 === 0 ? 0.7 : 0.45} fill={theme.isDark ? '#FFFFFF' : '#836B53'} opacity={dot.opacity} />
        ))}
      </Svg>
    </View>
  );
});

const styles = StyleSheet.create({
  ambientLayer: {
    position: 'absolute',
    left: -90,
    top: -90,
    width: AMBIENT_W,
    height: AMBIENT_H,
  },
  edgeShadeTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 150,
    backgroundColor: 'rgba(255,255,255,0.008)',
  },
  edgeShadeBottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 210,
    backgroundColor: 'rgba(0,0,0,0.10)',
  },
  edgeShadeBottomDaylight: {
    backgroundColor: 'rgba(126,104,79,0.035)',
  },
  grain: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    opacity: 0.022,
  },
});

export default ChatAtmosphere;
